import {MutableRefObject, useEffect, useRef, useState} from "react";
import {useAppContext} from "../../context/Context";
import api, {api_stream} from "../../utils/api";
import {useNavigate, useParams} from "react-router-dom";
import {ACTION, CHAT_MODE, GENERIC_MODAL_ACTIONS, MESSAGE_TYPE} from "../../context/actionTypes";
import {setBottomBarError, setBottomBarWarning} from "../../utils/Functions";
import {isChatMode} from "../../components/chat/fonctions";
import {assumeProcessedState, ChatMessage, MessageState, RagOutput} from "../../types/chat";
import {AxiosError, HttpStatusCode} from "axios";
import {DEFAULT_INTRODUCTION_TEXT} from "../../components/administration/settings/introductionMessage/useIntroductionMessage";


export default function useChat() {
    const [introductionText, setIntroductionText] = useState(DEFAULT_INTRODUCTION_TEXT);
    const n = 2;
    const [{user, isAwaitingCookieAuth}, dispatch] = useAppContext();
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef(null);
    const [isLargerThanInitial, setIsLargerThanInitial] = useState(false);
    const [isLargerThanInitialOnModify, setisLargerThanInitialOnModify] = useState(false);
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const [statusMessage, setstatusMessage] = useState<string | null>(null);
    const [initialHeight, setInitialHeight] = useState(0);
    const firstSearchMessage = "Utilisez notre moteur de recherche sémantique en saisissant votre question ou une phrase clé : il analysera le sens de votre demande et vous proposera les documents les plus pertinents.";

    const [MessageSearch, setMessageSearch] = useState(firstSearchMessage);
    const {id} = useParams();
    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        const storedMessages = localStorage.getItem("conversation");
        return storedMessages ? JSON.parse(storedMessages) : [];
    });

    const message = useRef(null);
    const [withHistory, setWithHistory] = useState(() => {
        const storedHistory = localStorage.getItem("keepHistory");
        return storedHistory ? JSON.parse(storedHistory) : false;
    });
    // Initialised at undefined when the request isn't completed yet. It gets set to true of false depending on if the company has the function or not.
    const [hasLLMChatPureOption, setHasLLMChatPureOption] = useState<boolean | undefined>();
    const [mode, setMode] = useState(() => {
        const storedMode = localStorage.getItem("mode");
        return storedMode ? JSON.parse(storedMode) : CHAT_MODE.QUERY;
    });
    const chatMode = isChatMode(mode);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [searchResponse, setSearchResponse] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [selectedModifiedMessage, setselectedModifiedMessage] = useState(null);
    const [controller, setcontroller] = useState(new AbortController());
    const [loggedInEndUsers, setLoggedInEndUsers] = useState(false);
    const navigate = useNavigate();


    const handleMessageChange = () => {
        if (message.current?.value.trim() === "" || isWaitingForResponse) {
            setIsButtonDisabled(true);
        } else {
            setIsButtonDisabled(false);
        }
    };

    useEffect(() => {
        // The messages with the PROCESSING state are replaced by ERROR on save.
        // If the page is not unloaded, the state will then be replaced by PROCESSED when we get a response
        const saveMessages: ChatMessage[] = messages
            .map((message: ChatMessage) => {
                // To not affect the displayed state, make a copy
                const copy = {...message};
                if (message.state === MessageState.PROCESSING) {
                    copy.state = MessageState.ERROR;
                }
                return copy;
            });
        localStorage.setItem("conversation", JSON.stringify(saveMessages));

        // Set the last machine message as selected by default, or when a new message is added
        setSelectedMessage(messages.slice().reverse().findIndex(message => message?.type === MESSAGE_TYPE.MACHINE));
    }, [messages, messages.length]);

    useEffect(() => {
        localStorage.setItem("keepHistory", JSON.stringify(withHistory));
    }, [withHistory]);

    useEffect(() => {
        if (hasLLMChatPureOption === false && mode === CHAT_MODE.QUERY_WITHOUT_RAG) {
            setMode(CHAT_MODE.QUERY);
        }
    }, [hasLLMChatPureOption, mode]);

    useEffect(() => {
        localStorage.setItem("mode", JSON.stringify(mode));
        // TODO 'Historique' to be added. For now enable it only when using pure LLM
        if (mode !== CHAT_MODE.QUERY_WITHOUT_RAG) {
            setWithHistory(false);
        }
    }, [mode]);

    useEffect(() => {
        if (message.current) {
            setInitialHeight(message.current.offsetHeight);
        }
    }, []);

    useEffect(() => {
        handleMessageChange();

        function scroll() {
            bottomRef?.current?.scrollIntoView({behavior: "smooth"});
        }

        if (isWaitingForResponse) {
            scroll();
            const timeout = setTimeout(scroll, 500);
            return () => clearTimeout(timeout);
        }
    }, [isWaitingForResponse]);

    useEffect(() => {
        function scroll() {
            bottomRef?.current?.scrollIntoView({behavior: "smooth"});
        }

        scroll();
        const timeout = setTimeout(scroll, 500);
        return () => clearTimeout(timeout);
    }, [chatMode]);

    const toggleHistory = () => {
        setWithHistory((old) => !old);
        /*
        TODO: Check if needed
        if (withHistory) {
            setWithHistory((old) => !old);
        }
        else {
            setConfirmModal({
                text: "Cette option inclut automatiquement les deux derniers messages en contexte.\n\nAttention : Peut provoquer une baisse de précision, cette fonctionnalité est toujours en test.",
                yes: () => (setWithHistory((old) => !old), setConfirmModal(null)),
                no: () => setConfirmModal(null)
            })
        }*/
    };

    const autoAdaptTextArea = (event) => {
        if (!event.target && !event.current) {
            return;
        }
        const targetToChange = event.target ? event.target : event.current;
        const largerFunc = targetToChange.id == "isButtonDisabled" ? setIsLargerThanInitial : setisLargerThanInitialOnModify;
        targetToChange.style.height = "auto"; // Allow the size to reduce
        targetToChange.style.height = `${targetToChange.scrollHeight}px`; // Make the textbox size grow if needed
        largerFunc(targetToChange.offsetHeight > initialHeight); // Reposition the send button
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
        autoAdaptTextArea(event);
    };

    const streaming_messages=async (reader: ReadableStreamDefaultReader<Uint8Array>,aiResponse: ChatMessage,messageToSend: ChatMessage, history = [], modeOverwrite: string, listMessages: ChatMessage[])=>{
        const response = await api_stream(`/request/${modeOverwrite}/${id}`, {
            method: "POST", signal: controller.signal, body: JSON.stringify({
                query: messageToSend.message,
                history,
            }),
        });

        if (!response.headers.get("Content-Type")?.includes("text/event-stream")) {
            const data = await response.json();
            setBottomBarError(dispatch, data.detail || data);
            messageToSend.state = MessageState.ERROR;
            setMessages([...listMessages]);
            return;
        }

        reader = response.body.getReader(); //get stream reader
        const decoder = new TextDecoder("utf-8"); //initializing the decoder

        while (true) {
            const {value, done} = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, {stream: true}); // decode the message to chunks
            for (const [, data] of Array.from(chunk.matchAll(/data: (.*?)\n\n/g))) {
                if (data === "[DONE]") {
                    break; // Handle end of stream
                }
                const element = JSON.parse(data);


                if (element.status_message) {
                    setstatusMessage(element.status_message);
                }
                if (element.chunk) {
                    setstatusMessage(null);
                    if (aiResponse.message == null) aiResponse.message = "";
                    aiResponse.message += element.chunk;
                }
                if (element.rag) {
                    aiResponse.rag = Object.values(element.rag);
                }
                if (element.warning) {
                    setBottomBarWarning(dispatch, element.warning);
                }
                if (element.error) {
                    throw new Error(element.error);
                }
            }
            if (aiResponse.message == null) continue;
            setMessages([...listMessages, aiResponse]);
        }

        aiResponse.state = MessageState.PROCESSED;
        messageToSend.state = MessageState.PROCESSED;// messageToSend is in listMessages
    }

    const sendQuery = async (messageToSend: ChatMessage, history = [], modeOverwrite: string, listMessages: ChatMessage[]) => {
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        const aiResponse: ChatMessage = {
            type: MESSAGE_TYPE.MACHINE,
            message: null,
            rag: [],
            state: MessageState.PROCESSING,
        };
        try {
            await streaming_messages(reader,aiResponse,messageToSend,history,modeOverwrite,listMessages)
            if (aiResponse.message != null) {
                setMessages([...listMessages, aiResponse]);
            }
        } catch (error: any) {
            if (!controller.signal.aborted) {
                console.log("Error sending message: ", error.name);
                setBottomBarError(dispatch, error);
                messageToSend.state = MessageState.ERROR;
                setMessages([...listMessages]);
            }
        } finally {
            if (controller.signal.aborted) {
                if (aiResponse.message === null) {
                    listMessages[listMessages.length - 1].state = MessageState.ERROR;
                    setMessages(listMessages);
                } else {
                    listMessages[listMessages.length - 1].state = MessageState.PROCESSED;
                    aiResponse.state = MessageState.PROCESSED;
                    setMessages([...listMessages, aiResponse]);
                }
                setcontroller(new AbortController());
            } else if (reader != null) {
                await reader.cancel();
            }
        }
    };

    const sendSearch = async (messageToSend: ChatMessage, history = [], modeOverwrite: string, listMessages: ChatMessage[]) => {
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        const aiResponse: ChatMessage = {
            type: MESSAGE_TYPE.MACHINE,
            message: null,
            rag: [],
            state: MessageState.PROCESSING,
        };
        try {
            await streaming_messages(reader,aiResponse,messageToSend,history,modeOverwrite,listMessages)
            if (aiResponse.rag?.length === 0) setMessageSearch("Aucun document ne correspond à votre recherche. Si vous ne trouvez pas l’information souhaitée, n’hésitez pas à essayer une autre formulation ou à préciser davantage votre recherche.");
            aiResponse.state = MessageState.PROCESSED;
            setSearchResponse(aiResponse);
        } catch (error: any) {
            if (!controller.signal.aborted) {
                setBottomBarError(dispatch, error);
                setSearchResponse(null);
                setMessageSearch(firstSearchMessage);
            }
        } finally {
            if (controller.signal.aborted) {
                setMessageSearch(firstSearchMessage);
                setSearchResponse(null);
                setBottomBarWarning(dispatch, "La recherche a été annulée.");
                setcontroller(new AbortController());
            } else if (reader != null) {
                await reader.cancel();
            }
        }
    };

    const handleStop = () => {
        try {
            controller.abort();
            console.log("Request stopped");
            setIsWaitingForResponse(false);
            setstatusMessage(null);
        } catch (error) {
        }
    };


    const handleSend = async () => {
        if (isButtonDisabled) return;
        setIsWaitingForResponse(true);
        setstatusMessage("");
        const humanMessage: ChatMessage = {
            type: MESSAGE_TYPE.HUMAN,
            message: message.current.value,
            date: new Date().toISOString(),
            state: MessageState.PROCESSING,
        };
        if (isChatMode(mode)) {
            const prevMessages = messages;
            const listMessages = [...prevMessages, humanMessage];
            setMessages(listMessages);
            const processedMessages = messages.filter(message => assumeProcessedState(message));

            message.current.value = "";
            autoAdaptTextArea(message);

            const history = withHistory
                ? processedMessages
                    .slice(Math.max(processedMessages.length - n, 1)) // Remove the introduction message
                    .map(element => ({
                        message: element.message,
                        sender: element.type,
                    }))
                : [];
            await sendQuery(humanMessage, history, (mode === CHAT_MODE.QUERY && withHistory) ? CHAT_MODE.QUERY_WITH_HISTORY : mode, listMessages);
        } else {
            await sendSearch(humanMessage, [], mode, []);
        }
        setIsWaitingForResponse(false);
        setstatusMessage(null);
    };

    const createFeedback = (question: ChatMessage, response: ChatMessage) => {
        dispatch({
            type: ACTION.SET_GENERIC_MODAL,
            payload: {
                props: {question, response, companyId: id},
                action: GENERIC_MODAL_ACTIONS.CREATE_FEEDBACK,
            },
        });
    };

    const handleRetry = async (index: number) => {
        if (isWaitingForResponse) return;
        setIsWaitingForResponse(true);
        setstatusMessage("");

        // Get all previous messages including the current one
        const updatedMessages = messages.slice(0, index + 1);
        updatedMessages[index].state = MessageState.PROCESSING;
        // Remove all following messages
        setMessages(updatedMessages);

        const messageToSend = updatedMessages[index];

        //History with the 2 messages before the current one
        const processedMessages = updatedMessages.filter(message => assumeProcessedState(message));
        const history = withHistory
            ? processedMessages
                .slice(Math.max(processedMessages.length - n, 1))
                .map(element => ({
                    message: element.message,
                    sender: element.type,
                }))
            : [];
        const listMessages = updatedMessages;
        await sendQuery(messageToSend, history, (mode === CHAT_MODE.QUERY && withHistory) ? CHAT_MODE.QUERY_WITH_HISTORY : mode, listMessages);
        setIsWaitingForResponse(false);
        setstatusMessage(null);
    };
    const handleModify = async (index: number, message: MutableRefObject<any>) => {
        if (isWaitingForResponse) return;
        setIsWaitingForResponse(true);
        setstatusMessage("");
        setselectedModifiedMessage(null);
        // Get all previous messages including the current one
        const updatedMessages = messages.slice(0, index + 1);
        updatedMessages[index].state = MessageState.PROCESSING;
        // Remove all following messages
        setMessages(updatedMessages);
        //History with the 2 messages before the current one
        const processedMessages = updatedMessages.filter(message => assumeProcessedState(message));
        const history = withHistory
            ? processedMessages
                .slice(Math.max(processedMessages.length - n, 1))
                .map(element => ({
                    message: element.message,
                    sender: element.type,
                }))
            : [];
        const listMessages = updatedMessages;
        // Make the new modified message
        const humanMessage: ChatMessage = {
            type: MESSAGE_TYPE.HUMAN,
            message: message.current.value,
            date: new Date().toISOString(),
            state: MessageState.PROCESSING,
        };
        const messageToSend = humanMessage;
        listMessages[index] = humanMessage;
        // Send the Message
        await sendQuery(messageToSend, history, (mode === CHAT_MODE.QUERY && withHistory) ? CHAT_MODE.QUERY_WITH_HISTORY : mode, listMessages);
        setIsWaitingForResponse(false);
        setstatusMessage(null);
    };

    const deleteConversation = () => {
        setConfirmModal({
            text: "Êtes-vous sûrs de vouloir supprimer la conversation ? Cette action est irréversible.",
            yes: () => {
                localStorage.removeItem("conversation");
                setMessages([]);
                setConfirmModal(null);
            },
            no: () => setConfirmModal(null),
        });
    };


    const displayRag = (rag: RagOutput, showPercentages: boolean) => {
        dispatch({
            type: ACTION.SET_GENERIC_MODAL,
            payload: {
                action: GENERIC_MODAL_ACTIONS.DISPLAY_RAG,
                props: {rag, showPercentages, companyUUID: id},
            },
        });
    };

    useEffect(() => {
        if (isAwaitingCookieAuth) {
            return;
        }
        if (user && user.company.uuid !== id) {
            navigate(`/chat/${user.company.uuid}`, {replace: true});
            return;
        }
        (async function() {
            try {
                const response = await api.get(`/request/chatbot-configuration/${id}`);
                const {introduction_text, llm_without_chat_enabled, logged_in_end_users} = response.data;
                setIntroductionText(introduction_text || DEFAULT_INTRODUCTION_TEXT);
                setHasLLMChatPureOption(llm_without_chat_enabled);
                setLoggedInEndUsers(logged_in_end_users);
            } catch (e) {
                setIntroductionText(DEFAULT_INTRODUCTION_TEXT);
                if (e instanceof AxiosError && (e.response.status === HttpStatusCode.NotFound || e.response.status === HttpStatusCode.UnprocessableEntity)) {
                    navigate("/administration");
                    setBottomBarError(dispatch, "Le lien est invalide, vous avez été redirigé vers l'accueil.");
                    return;
                }
                setBottomBarError(dispatch, e);
            }
        })().catch(console.error);
    }, [dispatch, id, isAwaitingCookieAuth, navigate, user]);

    return [{
        message,
        messages,
        chatContainerRef,
        bottomRef,
        isLargerThanInitial,
        withHistory,
        hasLLMChatPureOption,
        isWaitingForResponse,
        mode,
        selectedMessage,
        searchResponse,
        confirmModal,
        introductionText,
        isButtonDisabled,
        statusMessage,
        MessageSearch,
        selectedModifiedMessage,
        isLargerThanInitialOnModify,
        controller,
        id,
        loggedInEndUsers,
    }, {
        handleKeyDown,
        handleMessageChange,
        autoAdaptTextArea,
        handleSend,
        createFeedback,
        handleRetry,
        setMode,
        setSelectedMessage,
        deleteConversation,
        displayRag,
        setConfirmModal,
        toggleHistory,
        dispatch,
        setselectedModifiedMessage,
        handleModify,
        handleStop,
    }];
}
